import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface User {
  id: number
  uuid: string
  email: string
  firstName: string
  lastName: string
  fullName: string
  username: string
  phone?: string
  gender?: string
  dateOfBirth?: string
  bio?: string
  profileImage?: string
  coverImage?: string
  role: 'USER' | 'ADMIN' | 'SUPER_ADMIN'
  userType?: 'USER' | 'DOCTOR' | 'HEALTH_WORKER' | 'NGO' | 'NIKSHAY_MITRA'
    | 'SUPPORTER_INDIVIDUAL' | 'SUPPORTER_NGO' | 'SUPPORTER_CORPORATE'
    | 'NUTRITION_SPONSOR' | 'CSR_PARTNER' | 'VOLUNTEER_COMMUNITY'
  profileCompleted?: boolean
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'BANNED'
  emailVerified: boolean
  phoneVerified: boolean
  createdAt: string
}

interface AuthState {
  user: User | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  /** True once zustand `persist` has finished rehydrating from localStorage.
   *  On a full page load (direct URL / hard reload / shared deep link) the store
   *  starts at its default (isAuthenticated=false) and the persisted state is
   *  applied in a microtask AFTER the first React render. Route guards must wait
   *  for this flag before redirecting, otherwise a logged-in user opening a
   *  shared /post/:id or /reel/:id is wrongly sent to /login. Not persisted. */
  hasHydrated: boolean
  /** Transient: the role chosen on the Register page, used to route the user to
   *  the correct post-register destination (role onboarding or supporter form)
   *  WITHOUT a programmatic navigate that races with the /register route guard.
   *  Not persisted — cleared once consumed by CompleteProfilePage. */
  pendingRole: string | null

  // Actions
  setUser: (user: User | null) => void
  setToken: (token: string | null) => void
  setRefreshToken: (refreshToken: string | null) => void
  login: (user: User, token: string, refreshToken: string) => void
  logout: () => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  updateProfile: (user: Partial<User>) => void
  setPendingRole: (role: string | null) => void
  clearPendingRole: () => void
  setHasHydrated: (v: boolean) => void
  needsProfileCompletion: () => boolean
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      hasHydrated: false,
      pendingRole: null,

      setUser: (user) => set({ user }),

      setToken: (token) => set({ token }),

      setRefreshToken: (refreshToken) => set({ refreshToken }),

      login: (user, token, refreshToken) =>
        set({
          user,
          token,
          refreshToken,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        }),

      logout: () =>
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        }),

      setLoading: (isLoading) => set({ isLoading }),

      setError: (error) => set({ error, isLoading: false }),

      updateProfile: (userData) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null,
        })),

      setPendingRole: (role) => set({ pendingRole: role }),
      clearPendingRole: () => set({ pendingRole: null }),

      setHasHydrated: (v) => set({ hasHydrated: v }),

      needsProfileCompletion: () => {
        const { user } = get()
        if (!user) return false
        // ADMIN and SUPER_ADMIN don't need to complete profile
        if (user.role === 'ADMIN' || user.role === 'SUPER_ADMIN') return false
        // Check if userType is selected
        return !user.profileCompleted && !user.userType
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
      // Flip `hasHydrated` true once persist has rehydrated from localStorage.
      // Route guards (ProtectedRoute/AdminRoute) wait on this so a full page
      // load doesn't redirect a logged-in user away from a deep link before the
      // persisted auth state is applied. hasHydrated itself is not persisted.
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true)
      },
    }
  )
)